// The bounded sidebar tone palette — one home for every colour a panel can use,
// so a config selects a NAME and never a class. Kept out of primitives.tsx so
// that file exports components only (fast-refresh).

import type { BadgeTone } from '@clarix/shared-types';

/** Count / traffic-light pill: background + text. */
export const TONE_PILL: Record<BadgeTone, string> = {
  gray: 'bg-gray-100 text-gray-600',
  slate: 'bg-slate-100 text-slate-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  teal: 'bg-teal-100 text-teal-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  green: 'bg-green-100 text-green-700',
  orange: 'bg-orange-100 text-orange-700',
};

/** Leading status dot. */
const TONE_DOT: Record<BadgeTone, string> = {
  gray: 'bg-gray-300',
  slate: 'bg-slate-400',
  indigo: 'bg-indigo-400',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
  green: 'bg-green-500',
  orange: 'bg-orange-400',
};

/** Leading icon tint, for icons that carry meaning of their own. */
const TONE_TEXT: Record<BadgeTone, string> = {
  gray: 'text-gray-400',
  slate: 'text-slate-500',
  indigo: 'text-indigo-500',
  emerald: 'text-emerald-500',
  teal: 'text-teal-500',
  amber: 'text-amber-500',
  red: 'text-red-500',
  green: 'text-green-500',
  orange: 'text-orange-500',
};

/** Static (JIT-safe) dot background class for a tone. */
export function dotToneClass(tone: BadgeTone): string {
  return TONE_DOT[tone];
}

/** Static (JIT-safe) icon text-colour class for a tone. */
export function iconToneClass(tone: BadgeTone): string {
  return TONE_TEXT[tone];
}

/** Resolve a traffic-light tone from a value + [warn, danger] thresholds. */
export function trafficTone(value: number, thresholds: [number, number] = [1, 10]): BadgeTone {
  const [warn, danger] = thresholds;
  if (value >= danger) return 'red';
  if (value >= warn) return 'amber';
  return 'green';
}
